/**
 * 空仓库引导组件
 * 当仓库没有任何提交时，显示此引导页面帮助用户快速上手
 */

import { useState } from 'react';
import { PushTroubleshootingCallout } from './PushTroubleshootingCallout';
import './EmptyRepoGuide.css';

interface EmptyRepoGuideProps {
    repoName: string;
    repoFullPath: string;
    userName?: string;
    userEmail?: string;
    baseUrl?: string;
}

export function EmptyRepoGuide({
    repoName,
    repoFullPath,
    userName = '你的用户名',
    userEmail = 'your@email.com',
    baseUrl = window.location.origin,
}: EmptyRepoGuideProps) {
    const [configTab, setConfigTab] = useState<'local' | 'global'>('local');
    const [copiedUrl, setCopiedUrl] = useState(false);
    const [copiedBlock, setCopiedBlock] = useState<string | null>(null);

    const httpsUrl = `${baseUrl}/${repoFullPath}.git`;

    const handleCopyUrl = async () => {
        try {
            await navigator.clipboard.writeText(httpsUrl);
            setCopiedUrl(true);
            setTimeout(() => setCopiedUrl(false), 2000);
        } catch (err) {
            console.error('复制失败:', err);
        }
    };

    const handleCopyBlock = async (blockId: string, code: string) => {
        try {
            await navigator.clipboard.writeText(code);
            setCopiedBlock(blockId);
            setTimeout(() => setCopiedBlock(null), 2000);
        } catch (err) {
            console.error('复制失败:', err);
        }
    };

    // 代码块内容
    const configLocalCode = `git config --local user.name "${userName}"
git config --local user.email "${userEmail}"`;

    const configGlobalCode = `git config --global user.name "${userName}"
git config --global user.email "${userEmail}"`;

    const createNewRepoCode = `git clone ${httpsUrl}
cd ${repoName}
touch README.md
git add README.md
git commit -m "add README"
git push --set-upstream origin main`;

    const pushExistingFolderCode = `cd existing_folder
git init --initial-branch=main --object-format=sha1
git remote add origin ${httpsUrl}
git add .
git commit -m "Initial commit"
git push --set-upstream origin main`;

    const pushExistingRepoCode = `cd existing_repo
git remote rename origin old-origin
git remote add origin ${httpsUrl}
git push --set-upstream origin --all
git push --set-upstream origin --tags`;

    return (
        <div className="empty-repo-guide">
            <PushTroubleshootingCallout />
            {/* 空仓库提示 */}
            <div className="empty-repo-notice">
                <div className="notice-icon">📦</div>
                <div className="notice-content">
                    <h3>该仓库目前是空的</h3>
                    <p>克隆仓库或上传文件以开始使用。您也可以将现有代码推送到此仓库。</p>
                </div>
            </div>

            {/* Clone URL 面板 */}
            <div className="clone-panel">
                <h3>使用 HTTPS 克隆</h3>
                <div className="clone-url-row">
                    <input
                        type="text"
                        value={httpsUrl}
                        readOnly
                        onClick={(e) => (e.target as HTMLInputElement).select()}
                    />
                    <button
                        className={`copy-btn ${copiedUrl ? 'copied' : ''}`}
                        onClick={handleCopyUrl}
                    >
                        {copiedUrl ? '✓ 已复制' : '📋 复制'}
                    </button>
                </div>
            </div>

            {/* 命令行指南 */}
            <div className="guide-section">
                <h2>命令行指南</h2>
                <p>您可以使用以下命令将文件上传到仓库。</p>

                {/* 配置 Git 身份 */}
                <h3>配置 Git 身份</h3>
                <p>
                    <a href="https://git-scm.com/book/zh/v2" target="_blank" rel="noopener noreferrer">
                        开始使用 Git
                    </a>
                    {' '}并学习如何配置它。
                </p>

                <div className="guide-tabs">
                    <button
                        className={`guide-tab ${configTab === 'local' ? 'active' : ''}`}
                        onClick={() => setConfigTab('local')}
                    >
                        Local
                    </button>
                    <button
                        className={`guide-tab ${configTab === 'global' ? 'active' : ''}`}
                        onClick={() => setConfigTab('global')}
                    >
                        Global
                    </button>
                </div>

                <CodeBlock
                    id="config"
                    title={configTab === 'local' ? '仅对此项目配置 Git 身份' : '全局配置 Git 身份'}
                    code={configTab === 'local' ? configLocalCode : configGlobalCode}
                    copied={copiedBlock === 'config'}
                    onCopy={(code) => handleCopyBlock('config', code)}
                />

                {/* 添加文件 */}
                <h3>添加文件</h3>
                <p>使用 HTTPS 推送文件到仓库。</p>

                {/* 创建新仓库 */}
                <h4>
                    <span className="step-indicator">1</span>
                    创建新仓库
                </h4>
                <CodeBlock
                    id="create-new"
                    code={createNewRepoCode}
                    copied={copiedBlock === 'create-new'}
                    onCopy={(code) => handleCopyBlock('create-new', code)}
                />

                {/* 推送现有文件夹 */}
                <h4>
                    <span className="step-indicator">2</span>
                    推送现有文件夹
                </h4>
                <p>进入你的文件夹目录，然后配置 Git 仓库：</p>
                <CodeBlock
                    id="push-folder"
                    code={pushExistingFolderCode}
                    copied={copiedBlock === 'push-folder'}
                    onCopy={(code) => handleCopyBlock('push-folder', code)}
                />

                {/* 推送现有 Git 仓库 */}
                <h4>
                    <span className="step-indicator">3</span>
                    推送现有 Git 仓库
                </h4>
                <p>进入你的仓库目录，然后配置远程仓库：</p>
                <CodeBlock
                    id="push-repo"
                    code={pushExistingRepoCode}
                    copied={copiedBlock === 'push-repo'}
                    onCopy={(code) => handleCopyBlock('push-repo', code)}
                />
            </div>
        </div>
    );
}

/**
 * 代码块组件
 */
interface CodeBlockProps {
    id: string;
    title?: string;
    code: string;
    copied: boolean;
    onCopy: (code: string) => void;
}

function CodeBlock({ id: _id, title, code, copied, onCopy }: CodeBlockProps) {
    return (
        <div className="code-block">
            {title && (
                <div className="code-block-header">
                    <span className="code-block-title">{title}</span>
                    <button
                        className={`copy-btn ${copied ? 'copied' : ''}`}
                        onClick={() => onCopy(code)}
                    >
                        {copied ? '✓ 已复制' : '📋 复制'}
                    </button>
                </div>
            )}
            {!title && (
                <button
                    className={`copy-btn ${copied ? 'copied' : ''}`}
                    style={{ position: 'absolute', top: '0.5rem', right: '0.5rem' }}
                    onClick={() => onCopy(code)}
                >
                    {copied ? '✓ 已复制' : '📋 复制'}
                </button>
            )}
            <pre>
                <code>{code}</code>
            </pre>
        </div>
    );
}
